import type { LucideIcon } from 'lucide-react'
import { cn } from '@/utils/cn'

type Props = {
  title: string
  icon: LucideIcon
  accent?: string
  children: React.ReactNode
  className?: string
  headerExtra?: React.ReactNode
}

/** Card de seção alinhado ao padrão Financeiro/Clientes Agrada (glass + faixa superior). */
export function FinanceDetailSectionCard({
  title,
  icon: Icon,
  accent = '#ef4444',
  children,
  className,
  headerExtra,
}: Props) {
  return (
    <section className={cn('glass-card relative overflow-hidden p-5', className)}>
      <div
        className='absolute left-0 top-0 h-[2px] w-full'
        style={{
          backgroundImage: `linear-gradient(to right, transparent, ${accent}66, transparent)`,
        }}
      />
      <div className='mb-4 flex items-start justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div
            className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl'
            style={{ backgroundColor: `${accent}1f`, color: accent }}
          >
            <Icon size={18} aria-hidden />
          </div>
          <h3
            className='font-semibold uppercase tracking-wider text-[var(--font-color-label)]'
            style={{ fontSize: 'var(--font-size-label)' }}
          >
            {title}
          </h3>
        </div>
        {headerExtra}
      </div>
      {children}
    </section>
  )
}

type ValueTileProps = {
  label: string
  value: string
  accent?: string
  emphasized?: boolean
}

export function FinanceDetailValueTile({
  label,
  value,
  accent = '#ef4444',
  emphasized,
}: ValueTileProps) {
  return (
    <div className='relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.03] p-4'>
      <div
        className='absolute left-0 top-0 h-[2px] w-full opacity-80'
        style={{
          backgroundImage: `linear-gradient(to right, transparent, ${accent}88, transparent)`,
        }}
      />
      <p
        className='mb-1 font-medium uppercase tracking-wider text-[var(--font-color-label)]'
        style={{ fontSize: 'var(--font-size-label)' }}
      >
        {label}
      </p>
      <p
        className={cn(
          'text-xl font-black tabular-nums text-[var(--font-color-body)]',
          emphasized && 'text-accent',
        )}
      >
        {value}
      </p>
    </div>
  )
}

export function FinanceDetailInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-0.5 border-b border-white/[0.04] py-2.5 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:gap-4'>
      <span className='shrink-0 text-xs font-medium uppercase tracking-wider text-text-muted sm:w-36'>
        {label}
      </span>
      <span className='min-w-0 flex-1 text-sm font-medium text-text-primary sm:text-right'>
        {value}
      </span>
    </div>
  )
}

export const FINANCE_DETAIL_FIELD_CLASS =
  'w-full rounded-md border border-white/[0.06] bg-white/[0.04] px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-accent'
